"use client";

import * as React from "react";
import { formatPrice } from "../../lib/formatPrice";
import { cn } from "../../lib/utils";
import {
  PRICE_TRENDS_BARS_MIN_HEIGHT_PCT,
  PRICE_TRENDS_BARS_STYLES,
} from "./constants";
import type { PriceTrendsBarsProps } from "./types";

type Variant = "default" | "min" | "max";

export const PriceTrendsBars = React.forwardRef<
  HTMLElement,
  PriceTrendsBarsProps
>(
  (
    {
      className,
      title,
      description,
      items,
      currency = "USD",
      cheapestLabel = "Más barato",
      mostExpensiveLabel = "Más caro",
      showPrices = true,
      ...props
    },
    ref
  ) => {
    const { minId, maxId, max } = React.useMemo(() => {
      if (items.length === 0) {
        return { minId: null, maxId: null, max: 0 };
      }
      let cheapest = items[0];
      let priciest = items[0];
      for (const item of items) {
        if (item.price < cheapest.price) cheapest = item;
        if (item.price > priciest.price) priciest = item;
      }
      return { minId: cheapest.id, maxId: priciest.id, max: priciest.price };
    }, [items]);

    return (
      <section
        ref={ref}
        className={cn(PRICE_TRENDS_BARS_STYLES.root, className)}
        {...props}
      >
        {(title || description) && (
          <header className={PRICE_TRENDS_BARS_STYLES.header}>
            {title && (
              <h2 className={PRICE_TRENDS_BARS_STYLES.title}>{title}</h2>
            )}
            {description && (
              <p className={PRICE_TRENDS_BARS_STYLES.description}>
                {description}
              </p>
            )}
          </header>
        )}
        <ul className={PRICE_TRENDS_BARS_STYLES.list}>
          {items.map((item) => {
            const isMin = item.id === minId;
            const isMax = item.id === maxId;
            const variant: Variant = isMin ? "min" : isMax ? "max" : "default";
            const heightPct =
              max > 0
                ? Math.max(
                    (item.price / max) * 100,
                    PRICE_TRENDS_BARS_MIN_HEIGHT_PCT
                  )
                : PRICE_TRENDS_BARS_MIN_HEIGHT_PCT;
            const formattedPrice = formatPrice(item.price, currency);
            return (
              <li key={item.id} className={PRICE_TRENDS_BARS_STYLES.item}>
                {showPrices && (
                  <span className={PRICE_TRENDS_BARS_STYLES.price}>
                    {formattedPrice}
                  </span>
                )}
                <div className={PRICE_TRENDS_BARS_STYLES.barTrack}>
                  <div
                    data-testid={`price-trends-bar-${item.id}`}
                    data-variant={variant}
                    role="img"
                    aria-label={`${item.month}: ${formattedPrice}`}
                    className={cn(
                      PRICE_TRENDS_BARS_STYLES.bar,
                      PRICE_TRENDS_BARS_STYLES.barByVariant[variant]
                    )}
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
                <span className={PRICE_TRENDS_BARS_STYLES.month}>
                  {item.month}
                </span>
                {(isMin || isMax) && (
                  <span
                    className={cn(
                      PRICE_TRENDS_BARS_STYLES.badge,
                      isMin
                        ? PRICE_TRENDS_BARS_STYLES.badgeMin
                        : PRICE_TRENDS_BARS_STYLES.badgeMax
                    )}
                  >
                    {isMin ? cheapestLabel : mostExpensiveLabel}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    );
  }
);

PriceTrendsBars.displayName = "PriceTrendsBars";
